<script setup lang="ts">
  import type { PropType } from 'vue'

  import { computed } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils/core.utils'

  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import CPButton from '@ui/components/CPButton.vue'
  import CPText from '@ui/components/CPText.vue'

  import { NO_SEARCH_RESULTS_IMAGE } from '@courses/utility-layer/constants'

  const props = defineProps({
    searchQuery: {
      type: String,
      default: undefined,
    },
    variant: {
      type: String as PropType<'page' | 'dropdown'>,
      default: 'page',
    },
  })

  const emit = defineEmits<{
    clear: []
  }>()

  const { t } = useI18n()

  const isDropdown = computed(() => props.variant === 'dropdown')
  const illustrationSize = computed(() => (isDropdown.value ? 128 : 256))
  const containerClass = computed(() =>
    isDropdown.value
      ? 'flex w-full flex-col items-center justify-center gap-4 px-6 py-6 text-center overflow-x-hidden box-border'
      : 'flex min-h-[70vh] w-full flex-col items-center justify-center gap-4 px-6 py-12 text-center overflow-x-hidden box-border'
  )

  const handleClear = () => {
    emit('clear')
  }
</script>

<template>
  <div :id="createElementId('search', 'empty-state', 'container')" :class="containerClass">
    <OptimisedImage
      :id="createElementId('search', 'empty-state', 'illustration')"
      :src="NO_SEARCH_RESULTS_IMAGE"
      :alt="t('courses.search.emptyState.imageAlt')"
      :width="illustrationSize"
      :height="illustrationSize"
      :contain="true"
      gradient-mode="none"
      loading="eager"
      class="max-w-full"
      source="courses"
    />

    <div class="space-y-2">
      <CPText size="2xl" weight="semibold" class="text-body">
        {{ t('courses.search.emptyState.title', { query: searchQuery }) }}
      </CPText>

      <CPText size="lg" weight="regular" class="text-600">
        {{ t('courses.search.emptyState.description') }}
      </CPText>
    </div>

    <CPButton
      :id="createElementId('search', 'empty-state', 'clear')"
      type="secondary"
      color="secondary"
      size="md"
      class="mt-1"
      @click="handleClear"
    >
      {{ t('courses.search.emptyState.clearButton') }}
    </CPButton>
  </div>
</template>
